import type { SortState } from '../ui'

export type CsvTableRow = Record<string, unknown>
export type NormalizedCsvRow = Record<string, string>

export function resolveColumns(rows: CsvTableRow[], preferredColumns?: string[]): string[] {
  if (preferredColumns?.length) {
    return preferredColumns
  }

  const seen = new Set<string>()
  rows.forEach(row => {
    Object.keys(row).forEach(key => seen.add(key))
  })
  return Array.from(seen)
}

export function normalizeRows(
  rows: CsvTableRow[],
  columns: string[],
): NormalizedCsvRow[] {
  return rows.map(row => {
    const normalized: NormalizedCsvRow = {}
    columns.forEach(column => {
      const value = row[column]
      normalized[column] = value == null ? '' : String(value)
    })
    return normalized
  })
}

export function filterRows(rows: NormalizedCsvRow[], query: string): NormalizedCsvRow[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return rows
  return rows.filter(row =>
    Object.values(row).some(value => value.toLowerCase().includes(needle)),
  )
}

function asNumber(value: string): number | null {
  const cleaned = value.replace(/[$,%]/g, '').replace(/,/g, '').trim()
  if (!cleaned) return null
  const number = Number(cleaned)
  return Number.isFinite(number) ? number : null
}

export function sortRows(rows: NormalizedCsvRow[], sortState: SortState): NormalizedCsvRow[] {
  if (!sortState.column || !sortState.direction) return rows
  const direction = sortState.direction === 'asc' ? 1 : -1
  const column = sortState.column

  return [...rows].sort((left, right) => {
    const leftValue = left[column] ?? ''
    const rightValue = right[column] ?? ''
    const leftNumber = asNumber(leftValue)
    const rightNumber = asNumber(rightValue)
    if (leftNumber != null && rightNumber != null) {
      return (leftNumber - rightNumber) * direction
    }
    return leftValue.localeCompare(rightValue, undefined, { numeric: true }) * direction
  })
}

export function csvFromTableRows(rows: NormalizedCsvRow[], columns: string[]): string {
  const escape = (value: string) => {
    if (/[",\n]/.test(value)) {
      return `"${value.replace(/"/g, '""')}"`
    }
    return value
  }

  return [
    columns.map(escape).join(','),
    ...rows.map(row => columns.map(column => escape(row[column] ?? '')).join(',')),
  ].join('\n')
}

export function isNumericColumn(rows: NormalizedCsvRow[], column: string): boolean {
  const populated = rows.map(row => row[column]).filter(Boolean)
  return populated.length > 0 && populated.every(value => asNumber(value) != null)
}
